import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Calendar, Clock, MessageCircle, ArrowLeft, Share2, ChevronRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { articlesApi } from '../api/client';
import SEO from '../components/SEO';

export default function ArticleDetail() {
  const { firmSlug, articleId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    articlesApi.get(firmSlug, articleId).then(setData).catch(() => setNotFound(true));
  }, [firmSlug, articleId]);

  if (notFound) return <Navigate to="/artigos" replace />;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Carregando...</div>;

  const { article, firm } = data;
  const whatsappUrl = `https://wa.me/${firm.whatsapp}?text=${encodeURIComponent(`Olá! Li o artigo "${article.title}" no site e gostaria de uma consulta.`)}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.cover,
    datePublished: article.createdAt || article.date,
    author: { '@type': 'Person', name: firm.leadLawyer },
    publisher: { '@type': 'Organization', name: firm.name },
  };

  return (
    <div>
      <SEO title={article.title} description={article.excerpt} image={article.cover} type="article" jsonLd={jsonLd} />

      <article className="aj-wrap max-w-[820px] py-8">
        <nav className="flex items-center gap-1 text-xs text-[#52676e] mb-6">
          <Link to="/" className="hover:text-[#153e44]">Início</Link><ChevronRight className="w-3 h-3" />
          <Link to="/artigos" className="hover:text-[#153e44]">Artigos</Link><ChevronRight className="w-3 h-3" />
          <span className="truncate max-w-[280px]">{article.title}</span>
        </nav>

        <Link to="/artigos"><Button variant="outline" size="sm" className="mb-6"><ArrowLeft className="w-4 h-4 mr-1.5" /> Todos os artigos</Button></Link>

        <header className="mb-8">
          <h1 className="text-3xl md:text-5xl font-semibold text-[#153e44] leading-tight">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#425a62]">
            <div className="flex items-center gap-2">
              <img src={firm.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
              <div>
                <Link to={`/escritorio/${firm.slug}`} className="font-semibold text-[#153e44] hover:text-[#063f4c]">{firm.name}</Link>
                <div className="text-xs text-[#52676e]">{firm.leadLawyer}</div>
              </div>
            </div>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {article.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.readTime}</span>
          </div>
        </header>

        {article.cover && (
          <div className="aspect-[16/9] rounded-[10px] overflow-hidden mb-8 bg-[#f2f6f7]">
            <img src={article.cover} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        {article.content ? (
          <div className="prose prose-slate max-w-none prose-headings:text-[#153e44] prose-headings:font-semibold prose-a:text-[#075a6d] prose-strong:text-[#153e44] prose-p:leading-relaxed prose-p:text-[#354c54]">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>
        ) : (
          <>
            <p className="text-[#354c54] leading-relaxed text-lg">{article.excerpt}</p>
            {article.externalUrl && (
              <a href={article.externalUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-6" data-testid="article-external-link">
                <Button className="bg-[#153e44] hover:bg-[#0c3036]">Ler artigo completo no site do escritório</Button>
              </a>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-12 rounded-[10px] bg-white border border-[#cbd8dc] p-8 text-[#243b42]">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            <img src={firm.photo} alt="" className="w-16 h-16 rounded-[7px] object-cover border-2 border-[#2f5d65]" />
            <div className="flex-1">
              <div className="text-xs text-[#075a6d] font-semibold uppercase tracking-wider mb-1">Precisa de ajuda com este assunto?</div>
              <h3 className="text-xl font-semibold">Fale com {firm.name}</h3>
              <p className="text-[#425a62] text-sm mt-1">{firm.leadLawyer} • {firm.cityName}/{firm.state}</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Link to={`/escritorio/${firm.slug}`} className="flex-1 md:flex-none"><Button variant="outline" className="w-full border-[#2f5d65] bg-transparent text-[#122f36] hover:bg-[#edf4f5]"><User className="w-4 h-4 mr-2" /> Ver perfil</Button></Link>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex-1 md:flex-none"><Button className="w-full bg-[#25D366] hover:bg-[#1fb958]"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button></a>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={() => { navigator.clipboard.writeText(window.location.href); }}><Share2 className="w-4 h-4 mr-2" /> Compartilhar este artigo</Button>
        </div>
      </article>
    </div>
  );
}
